import Component from "../../lib/component";
import Nodalizer from "../../lib/nodalizer";
import { MoreFetcherButton } from "./more_fetcher_button";
import { resolveRelativeUrl } from "@rankers/helpers/url";


export const MoreFetcherConfig = {
  componentTypeKey: "MoreFetcher",
  baseHtmlClass: "more_fetcher",
  selector: ".more_fetcher",

  datasetConfigKey: "more_fetcher__config",
};

export class MoreFetcher extends Component {
  static get config() {
    return MoreFetcherConfig;
  }

  constructor(id_or_element) {
    super({ element: id_or_element, config: MoreFetcher.config });
    this.setDatasetConfig(MoreFetcher.config.datasetConfigKey);

    this.endpoint = this.getDatasetConfigValue("endpoint");
    this.containerSelector = this.getDatasetConfigValue("container_selector");
    this.excludeSelectors =
      this.getDatasetConfigValue("exclude_selectors") || []; // These are selectors that should be found in the Nodalizer response, and should be removed before moving the children to the container.
    this.buttonClasses = this.getDatasetConfigValue("button_classes");
    this.totalItemsCount = this.getDatasetConfigValue("total_items_count") || 0;

    this.nextPage = this.endpoint ? getPageFromUrl(this.endpoint) : 2; // The endpoint should have the page parameter set to 2 (because that's the next page), so we can start from there.

    this.button = getButtonComponent(this);

    this.isLoading = false;

    this.render();
  }

  fetchMore() {
    const endpoint = this.getEndpoint();
    if (!endpoint) {
      console.error("No endpoint specified for MoreFetcher");
      return;
    }

    const nodalizerMapping = {};
    nodalizerMapping[this.containerSelector] = this.getContainerElement(); // The target container is redundant here, but we keep it for clarity. After `prepare` we manually move the children to the container.

    this.isLoading = true;
    this.render();

    const nodalizer = new Nodalizer(endpoint, nodalizerMapping);
    nodalizer
      .prepare()
      .then((sourceElements) => {
        sourceElements.forEach((sourceElement) => {
          removeExcludeSelectors(sourceElement, this.excludeSelectors);
          moveChildrenToContainer(sourceElement, this.getContainerElement());
        });
      })
      .catch((error) => {
        console.error("Error fetching more content:", error);
      })
      .finally(() => {
        this.nextPage += 1; // Increment the page after fetching
        this.isLoading = false;
        this.render();
      });
  }

  getContainerElement() {
    return this.element.querySelector(this.containerSelector);
  }

  render() {
    if (this.button) this.button.render();
  }

  getFetchedItemsCount() {
    const containerElement = this.getContainerElement();
    const count = containerElement.childElementCount;
    return count - 1; // Exclude the button itself by subtracting 1
  }

  getItemsRemainingCount() {
    return this.totalItemsCount - this.getFetchedItemsCount();
  }

  // The initial endpoint has a `page` query parameter, but we need to remove it and then apply the correct page number.
  getEndpoint() {
    const url = new URL(resolveRelativeUrl(this.endpoint)); // Ensure the URL is fully qualified with the document's base URL
    const params = new URLSearchParams(url.search);
    params.set("page", this.nextPage);
    url.search = params.toString();
    return url.toString();
  }

}

function getButtonComponent(moreFetcher) {
  const containerElement = moreFetcher.getContainerElement();
  if (!containerElement) {
    console.error("[MoreFetcher] No container element found!");
    return null;
  }
  const nodeName = containerElement.lastElementChild.nodeName.toLowerCase();
  const options = {
    nodeName: nodeName,
    classNames: moreFetcher.buttonClasses,
  };
  const element = MoreFetcherButton.createElement(options);
  containerElement.appendChild(element);
  return new MoreFetcherButton(element, moreFetcher);
}

/**
 * Moves all children from the given element to the specified container.
 * @param element
 * @param container
 */
function moveChildrenToContainer(element, container) {
  // First we move the children to a document fragment
  const MAX_DELAY = 1000; // Maximum total delay in milliseconds
  const fragment = document.createDocumentFragment();
  let child;
  while ((child = element.firstChild) !== null) {
    if (child.nodeType === Node.ELEMENT_NODE) {
      child.style.opacity = 0; // Set initial opacity to 0
      child.style.transition = "opacity 1s ease-in"; // Set the transition so it fades in
    }
    fragment.appendChild(child);
  }

  // Append the fragment to the container (when you append a fragment, it moves the children)
  container.appendChild(fragment);

  // Trigger a fade-in effect with a delay for each child
  const elements = container.querySelectorAll("[style*='opacity']");
  const delayStep = Math.min(100, MAX_DELAY / elements.length); // Calculate delay step

  elements.forEach((element, index) => {
    setTimeout(() => {
      element.style.opacity = 1;
    }, index * delayStep);
  });

  // Remove the original (now empty) element after moving its children
  element.remove();
}

function removeExcludeSelectors(sourceElement, excludeSelectors) {
  excludeSelectors.forEach((selector) => {
    const elementsToRemove = sourceElement.querySelectorAll(selector);
    Array.from(elementsToRemove).forEach((element) => {
      element.remove();
    });
  });
}

function getPageFromUrl(urlString) {
  let url;
  try {
    url = new URL(resolveRelativeUrl(urlString)); //
  } catch (error) {
    console.error(`[MoreFetcher] Invalid URL '${urlString}':`, error);
    return 2; // Default to 2 if the URL is invalid
  }
  const params = new URLSearchParams(url.search);
  const pageParam = params.get("page");
  return pageParam ? parseInt(pageParam) : 2; // Default to 2 if no page parameter is found because we assume the first page is already loaded (hence "more" is needed).
}
